import type { Uuid } from "@/domains/global/identifiers";
import { retranscribeFromSource } from "@/modules/audio-recording/retranscribe-from-source";
import { getSourceBlobName } from "@/store/audio/transforming/source-blob-name";

import { TerminalOutcome } from "./audio-pipeline.types";
import { failResult, okResult, type StageResult } from "./stage-result";

/**
 * Retranscribe-stage adapter. Resolves the uploaded source blob, calls the
 * backend transcribe-from-source endpoint, and returns a typed outcome. The
 * backend is synchronous — the call resolves once the Temporal workflow has
 * produced a transcript (or failed).
 */
export async function runRetranscribeStage({
  audioRecordingId,
}: {
  audioRecordingId: Uuid;
}): Promise<StageResult> {
  const sourceBlobName = await getSourceBlobName({ audioRecordingId });
  if (!sourceBlobName) {
    return failResult(TerminalOutcome.RetranscribeFailed, {
      kind: "missing-source-blob",
      message: "Uploaded source blob is missing. Please retry processing.",
    });
  }

  const result = await retranscribeFromSource({ audioRecordingId, sourceBlobName });
  if (result.success) {
    return okResult();
  }

  return failResult(TerminalOutcome.RetranscribeFailed, {
    kind: "retranscribe-failed",
    message: result.errorMessage,
  });
}
